import React, { useMemo } from 'react'
import type { EChartsOption } from 'echarts'
import EChartsCanvas from '../core/EChartsCanvas'
import { useAnalyticsTheme } from '../theme/useAnalyticsTheme'
import { buildAnalyticsItemTooltip } from '../theme/echartsTheme'

export type AnalyticsDonutDatum = {
 id: string | number
 label: string
 value: number
 color?: string
}

export type AnalyticsDonutChartProps = {
 data: AnalyticsDonutDatum[]
 height?: number
 showLegend?: boolean
 variant?: 'pie' | 'donut'
 valueFormatter?: (value: number) => string
 loading?: boolean
 className?: string
}

const AnalyticsDonutChart: React.FC<AnalyticsDonutChartProps> = ({
 data,
 height = 300,
 showLegend = true,
 variant = 'donut',
 valueFormatter,
 loading = false,
 className,
}) => {
 const { mode, colors, rtl } = useAnalyticsTheme()
 const filtered = data.filter((d) => Number(d.value) > 0)
 const empty = filtered.length === 0

 const option = useMemo<EChartsOption>(() => {
  const pieColors = filtered.map((d, i) => d.color ?? colors.palette[i % colors.palette.length])
  return {
   backgroundColor: 'transparent',
   color: pieColors,
   tooltip: buildAnalyticsItemTooltip(colors, valueFormatter),
   legend: showLegend
    ? {
       bottom: 0,
       type: 'scroll',
       ...(rtl ? { right: 0, left: 'auto' } : { left: 'center', right: 'auto' }),
       align: rtl ? 'right' : 'auto',
       textStyle: { color: colors.label, fontSize: 11 },
      }
    : { show: false },
   series: [
    {
     type: 'pie',
     radius: variant === 'pie' ? '72%' : ['42%', '72%'],
     center: ['50%', variant === 'pie' && showLegend ? '44%' : '46%'],
     padAngle: 3,
     itemStyle: { borderRadius: 4, borderColor: colors.tooltipBg, borderWidth: 2 },
     label: { show: false },
     emphasis: {
      scale: true,
      scaleSize: 6,
      itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.15)' },
     },
     data: filtered.map((d) => ({ name: d.label, value: d.value })),
    },
   ],
  }
 }, [colors, filtered, rtl, showLegend, valueFormatter, variant])

 return (
  <EChartsCanvas
   option={option}
   height={height}
   className={className}
   loading={loading}
   empty={empty}
   themeKey={`${variant}-${mode}-${rtl ? 'rtl' : 'ltr'}`}
  />
 )
}

export default AnalyticsDonutChart
